import {Button} from '@/components/ui/button/Button'
import {DialogDismiss} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import dayjs, {formatLocaleDate} from '@/dayjs'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {useProjectMutationSuccess} from '@/features/projects/hooks/useProjectMutationSuccess'
import {useWorkingDays} from '@/features/projects/hooks/useWorkingDays'
import {HttpEditProjectDetailsPayload} from '@/features/projects/http/projects.http'
import {useEditProjectDetails} from '@/features/projects/queries/useEditProjectDetails'
import {assistiveRegimeToRehabCareCommitment} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import {errorHandler, ObjectKeys} from '@/utilities/helpers'
import {useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {Spinner} from '@/components/ui/spinner/Spinner'

const EditProjectDetailsFormFooterSchema = ProjectFieldsSchema.pick({
    id: true,
    projectType: true,
    sector: true,
    durationDays: true,
    assistiveRegime: true,
    actualDays: true,
    numberOfCycles: true,
    startDate: true,
    expirationDate: true
})
type EditProjectDetailsFormFooterSchema = z.infer<typeof EditProjectDetailsFormFooterSchema>

export const EditProjectDetailsFormFooter = ({onCancel}: {onCancel: () => void}) => {
    const {t} = useTranslation()
    const form = useFormContext<EditProjectDetailsFormFooterSchema>()
    const id = useWatch({control: form.control, name: 'id'})

    const startDate = useWatch({control: form.control, name: 'startDate'})
    const expirationDate = useWatch({control: form.control, name: 'expirationDate'})
    const durationDays = useWatch({control: form.control, name: 'durationDays'})

    const actualDays = useWorkingDays({
        startDate: startDate ? dayjs(startDate).startOf('day').toDate() : null,
        expirationDate: expirationDate ? dayjs(expirationDate).endOf('day').toDate() : null,
        durationDays: durationDays ?? undefined
    })

    const handleProjectMutationSuccess = useProjectMutationSuccess({
        projectId: id,
        onComplete: onCancel
    })

    const {mutate: editProjectDetails, isPending} = useEditProjectDetails({
        urlParams: {projectId: id},
        options: {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, id]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, id]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS, 'extended']})
                await handleProjectMutationSuccess()
            },
            onError: errorHandler
        }
    })

    const handleSubmit = form.handleSubmit(
        formValues => {
            editProjectDetails(
                HttpEditProjectDetailsPayload.parse({
                    type: formValues.projectType,
                    sector: formValues.sector,
                    durationDays: formValues.durationDays || null,
                    actualDays: actualDays || null,
                    numberOfCycles: formValues.numberOfCycles ? formValues.numberOfCycles : null,
                    startDate: formValues.startDate ? formatLocaleDate(formValues.startDate, 'YYYY-MM-DD') : null,
                    assistiveRegime: formValues.assistiveRegime ? formValues.assistiveRegime : null,
                    // This is sent to the backend just to allow the SIAR export, it is not a field of our form
                    rehabilitativeAssistanceCommitment:
                        formValues.sector == 'outpatient' && formValues.assistiveRegime
                            ? assistiveRegimeToRehabCareCommitment[formValues.assistiveRegime].toString()
                            : undefined,
                    expirationDate: formValues.expirationDate
                        ? formatLocaleDate(formValues.expirationDate, 'YYYY-MM-DD')
                        : null
                })
            )
        },
        error => {
            console.log('values', form.getValues())
            console.error(error)
        }
    )

    return (
        <Flexbox fullWidth align="center" justify="end" gap={2}>
            <DialogDismiss onClick={() => form.reset()} render={<Button variant="secondary" />}>
                {t('commons:close')}
            </DialogDismiss>
            <Button
                variant="primaryNeutral"
                onClick={handleSubmit}
                disabled={isPending || ObjectKeys(form.formState.dirtyFields).length == 0}
            >
                {t('commons:save')}
                {isPending && <Spinner />}
            </Button>
        </Flexbox>
    )
}
